import "./App.css";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useState, useRef } from "react";
import { Nav, Navbar, Offcanvas } from 'react-bootstrap';
import L, { LatLng, Icon, Map, LatLngExpression, Polyline } from "leaflet";
import "react-datepicker/dist/react-datepicker.css";
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import { library } from '@fortawesome/fontawesome-svg-core';
import { Logout, calculateVoltage, getAllDevicesDate, getLastestData, getData, DateFormatWithSlash, FormatStartDate, FormatEndDate, moveTo, addUsageRecord, getPayloadSourceData } from './ELTRES_cmnFunc';
import { DeviceData, Data, UsageRecord, PayloadSourceData } from "./ELTRES_cmnDefine";
import { Collapse } from "react-bootstrap";
import { LocalizationProvider, DateTimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import { machine } from "os";

//--------------Data from DB-------------------
type Pins = { 
  position: LatLng, 
  name: string, 
  devId: number, 
  dateTime: string, 
  adc: string,
  machineType: string | undefined,
  machineNum: number, 
  measuredVal : number,
  unit: string| undefined, 
  err : string| undefined, 
  visible: boolean, 
  color: string, 
  initialColor: string 
}[];

const initialPins: { 
  position: LatLng, 
  name: string, 
  devId: number, 
  dateTime: string, 
  adc: string,
  machineType: string | undefined, 
  machineNum: number, 
  measuredVal : number, 
  unit: string| undefined, 
  err : string| undefined, 
  visible: boolean, 
  color: string, 
  initialColor: string 
}[] = [];

interface Option {
  label: string
  value: number
  unit?: string
}

const errmsg: Option[] = [
  { label: "－－", value: 0 },
  { label: "電池残量少", value: 1 },
  { label: "高濃度", value: 2 },
  { label: "流量異常", value: 3 },
  { label: "管外れ", value: 4 },
  { label: "応答なし", value: 9 },
]

const machineType: Option[] = [
  { label: "サンプリングポンプ", value: 1 , unit: "mL/min" },
  { label: "測定器", value: 2, unit: "ppm" },
]

function createIcon(color: string) {
    return new Icon({
        iconUrl: require(`./img/${color}.png`),
        iconSize: [30, 30],
        iconAnchor: [15, 28],
        popupAnchor: [0, -30],
    });
}

library.add(faMapMarkerAlt);


export function MovingHistory(): JSX.Element {
    const uId = sessionStorage.getItem('u_id')?.toString();
    const authority = sessionStorage.getItem('authority')?.toString();
    const cIDSess = sessionStorage.getItem('client_id')?.toString();
    const client_id = cIDSess && !isNaN(parseInt(cIDSess)) ? parseInt(cIDSess) : null;
    const u_id = uId ? JSON.parse(uId).u_id : 0;
    const savedLayer = localStorage.getItem('selectedLayer');
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<Map | null>(null);
    const [layer, setLayer] = useState(savedLayer || '');
    const [pins, setPins] = useState(initialPins);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedDevIds, setSelectedDevIds] = useState<number[]>([]);
    const currentDate = new Date();
    const startTime = new Date(currentDate);
    const [startDate, setStartDate] = useState(FormatStartDate(currentDate));
    const [endDate, setEndDate] = useState(FormatEndDate(currentDate));
    startTime.setHours(currentDate.getHours() - 3)
    const allColors: string[] = ['red', 'blue', 'green', 'orange', 'purple', 'pink', 'blueViolet', 'yellow', 'lightBlue', 'lightGreen'];
    const [initialDevIdToColorMap, setInitialDevIdToColorMap] = useState<{ [key: number]: string }>({});
    const [currentColorMap, setCurrentColorMap] = useState<Record<number, string>>({});
    const [polylines, setPolylines] = useState<Polyline[]>([]);
    const devIdToLatLngsRef = useRef<Record<number, LatLngExpression[]>>({});
    const [allDevicesData, setAllDevicesData] = useState<DeviceData[]>([]);
    const [pldSourceData, setPldSourceData] = useState<PayloadSourceData[]>([]);
    const [deviceDataAvailability, setDeviceDataAvailability] = useState<Record<number, boolean>>({});
    const showAdminLinks = authority !== '2';
    const isClientAdmin = authority == '1';
    const [markerRefs, setMarkerRefs] = useState<globalThis.Map<number, L.Marker[]>>(new globalThis.Map());
    const [polylineRefs, setPolylineRefs] = useState<globalThis.Map<number, L.Polyline[]>>(new globalThis.Map());
    const [isLoading, setIsLoading] = useState(false);
    const fetchDoneRef = useRef(false);
    const isFirstRender = useRef(true);
    const hasAppliedNavState = useRef(false);
    const location = useLocation();
    const navigate = useNavigate();

    function calculateVisibleTiles(map: L.Map, client_id: number | null, uId: number) {
        const bounds = map.getBounds();
        const tileSize = 256;
        const nw = bounds.getNorthWest();
        const se = bounds.getSouthEast();

        const nwTile = map.project(nw, map.getZoom()).divideBy(tileSize).floor();
        const seTile = map.project(se, map.getZoom()).divideBy(tileSize).floor();

        const visibleTilesCount = (seTile.x - nwTile.x + 1) * (seTile.y - nwTile.y + 1);
        // console.log('Visible Tiles Count:', visibleTilesCount);
        localStorage.setItem('visibleTilesCount', JSON.stringify(visibleTilesCount));

        const usage: UsageRecord = {
            use_date: new Date().toISOString(),
            client_id: client_id,
            u_id: uId,
            event_type: 0,
            tile_count: visibleTilesCount
        };
        addUsageRecord(usage);
    };

    const colorFor = (devId: number) =>
        currentColorMap[devId] ??
        initialDevIdToColorMap[devId] ??
        allColors[devId % allColors.length];


    const fetchData = async () => {
      try {
        setIsLoading(true);
        fetchDoneRef.current = false;

        let devices = await getAllDevicesDate();
        if (!devices) devices = [];

        devices = devices.filter((d: any) => d.enable === true);
        if (isClientAdmin && cIDSess) {
          devices = devices.filter((d: any) => d.client_id == parseInt(cIDSess));
        }

        // 1) assign colors to devices and save（important：must be done before setPins）
        const initialMap: Record<number, string> = {};
        devices.forEach((d: DeviceData, i: number) => {
          initialMap[d.dev_id] = allColors[i % allColors.length];
        });
        setInitialDevIdToColorMap(initialMap);

        // 2) the first 10 devices：currentColor = defaultColor
        const initialDevIds : number[] = devices.slice(0, 10).map((d: DeviceData) => d.dev_id);
        setSelectedDevIds(initialDevIds);
        setCurrentColorMap(prev => {
          const next = { ...prev };
          initialDevIds.forEach(id => { next[id] = initialMap[id]; });
          return next;
        });

        // 3) get new data
        const dataAvailability: Record<number, boolean> = {};
        const latestDataPromises = devices.map(async (device: DeviceData) => {
          const latestData = await getLastestData(device.dev_id);
          if (
            latestData !== null &&
            latestData.latitude <= 90 && latestData.latitude >= -90 &&
            latestData.longitude <= 180 && latestData.longitude >= -180
          ) {
            dataAvailability[device.dev_id] = true;
            return latestData;
          } else {
            dataAvailability[device.dev_id] = false;
            return null;
          }
        });
        const latestDataResults = (await Promise.all(latestDataPromises)).filter(Boolean) as any[];

        // 4) pins will get color by mapping when drawing map(leaflet)
        const newPins = latestDataResults.map(data => ({
          position: new LatLng(parseFloat(data.latitude.toFixed(6)), parseFloat(data.longitude.toFixed(6))),
          name: devices.find((device: DeviceData) => device.dev_id === data.dev_id)?.dev_name || 'Unknown',
          devId: data.dev_id,
          dateTime: DateFormatWithSlash(new Date(data.tx_time)),
          adc: calculateVoltage(data.src_02),
          machineType: machineType.find((m) => m.value == data.src_01)? machineType.find((m) => m.value == data.src_01)?.label : " 不明 ",
          machineNum: data.src_05,
          measuredVal: data.src_03,
          unit: machineType.find((m) => m.value == data.src_01)? machineType.find((m) => m.value == data.src_01)?.unit : "",
          err: errmsg.find((e) => e.value == data.src_04) ? errmsg.find((e) => e.value == data.src_04)?.label :"不明",
          visible: true,
          initialColor: '',
          color: ''
        }));

        setDeviceDataAvailability(dataAvailability);
        setAllDevicesData(devices);
        setPins(newPins);
      } catch (error) {
        console.error('Error fetching data:', error);
        setIsLoading(false);
      } finally {
        fetchDoneRef.current = true;
      }
    };

    useEffect(() => {
        //---------------gruoupingPins----------------------
        const map: Record<number, LatLngExpression[]> = {};
        for (const p of pins) {
          if (!map[p.devId]) map[p.devId] = [];
          map[p.devId].push(p.position);
        }
        devIdToLatLngsRef.current = map;
    }, [pins]);

    //---------------render page-------------------
    useEffect(() => {
        fetchData();
    }, [])

    useEffect(() => {
      if (allDevicesData.length === 0) return;

      const initialMap: Record<number, string> = {};
      allDevicesData.forEach((d, i) => {
        initialMap[d.dev_id] = allColors[i % allColors.length];
      });
      setInitialDevIdToColorMap(initialMap);

      setCurrentColorMap(prev => {
        const next: Record<number, string> = { ...prev };

        // clean used color
        Object.keys(next).forEach(k => {
          const id = Number(k);
          if (!selectedDevIds.includes(id)) delete next[id];
        });

        // if default color be used, give an avaliable color
        const used = new Set(Object.values(next));
        selectedDevIds.slice(0, 10).forEach(id => {
          if (!next[id]) {
            const preferred = initialMap[id];
            const color =
              used.has(preferred)
                ? (allColors.find(c => !used.has(c)) || preferred)
                : preferred;
            next[id] = color;
            used.add(color);
          }
        });

        return next;
      });
    }, [allDevicesData, selectedDevIds]);



    //---------------render page-------------------
    useEffect(() => {
      if (isFirstRender.current) {
        const visibleTilesCount = localStorage.getItem('visibleTilesCount');
        const usage: UsageRecord = {
          use_date: new Date().toISOString(),
          client_id,
          u_id,
          event_type: 0,
          tile_count: visibleTilesCount ? parseInt(visibleTilesCount) : 0
        };
        addUsageRecord(usage);
        isFirstRender.current = false;
      }
    }, []); // only run once


    useEffect(() => {
        if (!fetchDoneRef.current) return;
        
        let selectedFromNav: number[] | null = null;
        let colorMapFromNav: Record<number, string> | null = null;

        if (!hasAppliedNavState.current) {  // only use location.state once
          const navState = (location.state as {
            selected?: number[];
            colorMap?: Record<number, string>;
          } | null) ?? null;
        
          if (navState?.selected && navState?.colorMap) {
            const valid = navState.selected
              .filter(id => allDevicesData.some(d => d.dev_id === id))
              .slice(0, 10);
          
            if (valid.length) {
              selectedFromNav = valid;
            
              const used = new Set<string>();
              const cm = navState.colorMap as Record<number, string>;
              const nextMap: Record<number, string> = {};
            
              valid.forEach(id => {
                const preferred = cm[id] ?? initialDevIdToColorMap[id];
                let use = preferred;
                if (used.has(use)) {
                  const free = allColors.find(c => !used.has(c));
                  if (free) use = free;
                }
                used.add(use);
                nextMap[id] = use;
              });
            
              colorMapFromNav = nextMap;
            
              setSelectedDevIds(valid);
              setCurrentColorMap(nextMap);
            
              navigate(location.pathname, { replace: true, state: null });
              hasAppliedNavState.current = true;
            }
          }
        }
      
        const effectiveSelected = selectedFromNav ?? selectedDevIds;
        const effectiveColorMap: Record<number, string> =
          colorMapFromNav ? { ...currentColorMap, ...colorMapFromNav } : currentColorMap;
      
        const colorForNow = (devId: number) =>
          effectiveColorMap[devId] ??
          initialDevIdToColorMap[devId] ??
          allColors[devId % allColors.length];

        if (mapRef.current) {
            const initialMap = L.map(mapRef.current);
            const storedZoom = localStorage.getItem('mapZoom');
            const storedCenterStr = localStorage.getItem('mapCenter');
            const storedCenter = storedCenterStr ? JSON.parse(storedCenterStr) : null;

            if (storedZoom && storedCenter) {
                initialMap.setView(storedCenter, parseInt(storedZoom));
            } else {
                initialMap.setView([34.3852, 132.4552], 15);
            }

            initialMap.on('zoomend', () => {
                const currentZoom = initialMap.getZoom();
                localStorage.setItem('mapZoom', currentZoom.toString());
            });

            initialMap.on('moveend', () => {
                const currentCenter = initialMap.getCenter();
                calculateVisibleTiles(initialMap, client_id, u_id)
                localStorage.setItem('mapCenter', JSON.stringify([currentCenter.lat, currentCenter.lng]));
            });

            setMap(initialMap);

            //-----------prepare baselayers--------------
            const mapLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                attribution: "Map data © <a href='https://openstreetmap.org'>OpenStreetMap</a> contributors"
            });

            const satelliteLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
                attribution: "Map data © <a href='https://openstreetmap.org'>OpenStreetMap</a> contributors"
            });

            const baseLayer = (layer === '地図') ? mapLayer : satelliteLayer;
            baseLayer.addTo(initialMap);

            L.control.layers({ '地図': mapLayer, '衛星写真': satelliteLayer }).addTo(initialMap);
            initialMap.on('baselayerchange', (event) => {
              setLayer(event.name);
            });
            
            const markers: L.Marker[] = [];
            const newPolylines: Polyline[] = [];
            
            if (pins.length > 0) {
              pins.forEach((pin) => {
                if (pin.visible) {
                  const selected = effectiveSelected.includes(pin.devId);
                  const colorToUse = colorForNow(pin.devId);
                    
                  const marker = L.marker(pin.position, {
                    icon: createIcon(colorToUse),
                      opacity: selected ? 1 : 0,
                      interactive: true 
                      // when creating marksers, interactive always set true (means popup active),
                      // but whether the popup is actually displayed will be based on CSS pointer-events
                    })
                    .addTo(initialMap)
                    
                    if (selected) {
                      marker.bindPopup(
                      `デバイス ：${pin.name} <br /> 送信日時 ：${pin.dateTime} <br /> 　　緯度 ：${pin.position.lat} <br /> 　　経度 ：${pin.position.lng} <br /> eltres電池：${pin.adc} <br /> 機器種別 ：${pin.machineType} <br /> 機器番号 ：${pin.machineNum} <br /> 　計測値 ：${pin.measuredVal} ${pin.unit}  <br /> 　エラー ：${pin.err}`
                    );
                  }
                    
                  // if unselected, popup will not display by CSS pointer-events:none
                  marker.on('add', () => {
                    const el = (marker as any).getElement?.();
                    if (el && !selected) el.style.pointerEvents = 'none'; 
                  });
                    
                  setMarkerRefs(prevRefs => {
                    const updatedRefs = new globalThis.Map(prevRefs);
                    const markers = updatedRefs.get(pin.devId) || [];
                    markers.push(marker);
                    updatedRefs.set(pin.devId, markers);
                    return updatedRefs;
                  });

                } else {
                  map?.eachLayer((layer) => {
                    if (layer instanceof L.Marker && layer.getLatLng().equals(pin.position)) {
                      map.removeLayer(layer);
                    }
                  })
                }
              });
                  
              const devIdToLatLngs = devIdToLatLngsRef.current;
              Object.keys(devIdToLatLngs).forEach((devIdStr) => {
                const devId = parseInt(devIdStr, 10);
                const pin = pins.find(p => p.devId === devId);
                if (!pin?.visible) return;
                
                const latlngs = devIdToLatLngs[devId];
                if (latlngs.length <= 1) return;
                
                const selected = effectiveSelected.includes(devId);
                const colorToUse = colorForNow(pin.devId);
                const polyline = L.polyline(latlngs, {
                  color: colorToUse,
                  opacity: selected ? 1 : 0,
                  interactive: selected,   // popup is unused when not selected
                }).addTo(initialMap);
                // DOM : close pointer-events
                polyline.on('add', () => {
                  const el = (polyline as any).getElement?.();
                  if (el && !selected) el.style.pointerEvents = 'none';
                });
                setPolylineRefs(prevRefs => {
                  const m = new globalThis.Map(prevRefs);
                  const arr = m.get(devId) ?? [];
                  arr.push(polyline);
                  m.set(devId, arr);
                  return m;
                });
                newPolylines.push(polyline); 
              });
              
              setPolylines(newPolylines);
            }
            
            const onTilesLoaded = () => setIsLoading(false);
            baseLayer.on('load', onTilesLoaded);

            return () => {
              baseLayer.off('load', onTilesLoaded);
              initialMap.remove();
              markers.forEach(marker => marker.remove());
              newPolylines.forEach(pl => pl.remove());
            };
        }
    }, [pins]);

    useEffect(() => {
        localStorage.setItem('selectedLayer', layer);
    }, [layer]);

    //--------------ViewAllPins-------------------
    const handleViewAllClick = () => {
      const selectedPins = pins.filter(pin => selectedDevIds.includes(pin.devId));
      if (!map || selectedPins.length === 0) return;

      const bounds = L.latLngBounds(selectedPins.map(pin => pin.position));

      // check Navbar's heigh anf give safe space for avoiding navbar covering pin
      const navEl = document.querySelector('.navbar') as HTMLElement | null;
      const topPad = (navEl?.offsetHeight ?? 56) + 20; // Navbar +12 px 

      const rightPad = 20;
      const leftPad = 20;
      const bottomPad = 20;

      map.fitBounds(bounds, {
        paddingTopLeft: [leftPad, topPad],
        paddingBottomRight: [rightPad, bottomPad],
        maxZoom: 18
      });
    };

    //--------------HandleVisibility-------------------
    const handleCheckboxChange = (devId: number) => {
      const isSelected = selectedDevIds.includes(devId);

      if (isSelected) {
        // un-check：delete from devId selected and currentColorMap(the color become available)
        setSelectedDevIds(prev => prev.filter(id => id !== devId));
        setCurrentColorMap(prev => {
          const next = { ...prev };
          delete next[devId];
          return next;
        });

        // undisplay pin/polylines(by setting opacity) & popup(by CSS pointer-events:none)
        const markers = markerRefs.get(devId);
        const polylines = polylineRefs.get(devId);
        markers?.forEach(marker => {
          marker.setOpacity(0);
          marker.closePopup();
          const el = (marker as any).getElement?.();
          if (el) el.style.pointerEvents = 'none';
        });
        polylines?.forEach(pl => {
          pl.setStyle({ opacity: 0 });
          const el = (pl as any).getElement?.();
          if (el) el.style.pointerEvents = 'none';
        });
        return;
      }

      if (selectedDevIds.length >= 10) {
        alert('デバイスは最大で10個しか選択できません。');
        return;
      }

      // default color be first, if the color already is used，get new color
      const preferred = initialDevIdToColorMap[devId];
      const used = new Set(Object.values(currentColorMap));
      const colorToUse = used.has(preferred)
        ? allColors.find(c => !used.has(c)) || preferred
        : preferred;

      setSelectedDevIds(prev => [...prev, devId]);
      // console.log("SelectedDevIds : " + selectedDevIds)
      setCurrentColorMap(prev => ({ ...prev, [devId]: colorToUse }));

      const markers = markerRefs.get(devId);
      const polylines = polylineRefs.get(devId);
      const pin = pins.find(p => p.devId === devId);

      // display pin(by setting opacity) & popup (by CSS pointer-events:auto)
      if (markers) {
        const updatedIcon = createIcon(colorToUse);
        markers.forEach(marker => {
          marker.setIcon(updatedIcon);
          marker.setOpacity(1);
        if (pin) {
          marker.unbindPopup();
          marker.bindPopup(
            `デバイス ：${pin.name} <br /> 送信日時 ：${pin.dateTime} <br /> 　　緯度 ：${pin.position.lat} <br /> 　　経度 ：${pin.position.lng} <br /> eltres電池：${pin.adc} <br /> 機器種別 ：${pin.machineType} <br /> 機器番号 ：${pin.machineNum} <br /> 　計測値 ：${pin.measuredVal} ${pin.unit}  <br /> 　エラー ：${pin.err}`
          );
        }
        const el = (marker as any).getElement?.();
          if (el) el.style.pointerEvents = 'auto';
        });
      }
      if (polylines) {
        polylines.forEach(polyline => {
          polyline.setStyle({ opacity: 1, color: colorToUse });
          const el = (polyline as any).getElement?.();
          if (el) el.style.pointerEvents = 'auto';
        });
      }
    };


    //----------------SearchClick-------------------

    const fetchDataForTimeRange = async (startDate: string, endDate: string, selectedDevIds: number[]) => {
        let allPins: Pins = [];
        let dataAvailability: Record<number, boolean> = { ...deviceDataAvailability };

        for (const devId of selectedDevIds) {
            try {
              const datas = await getData(devId, startDate, endDate)
              const colorToUse = currentColorMap[devId] ?? initialDevIdToColorMap[devId];
              const validPinsForDevice = datas.filter((d: Data) => {
                  return d.latitude <= 90 && d.latitude >= -90 && d.longitude <= 180 && d.longitude >= -180;
              }).map((d: Data) => ({
                  position: new LatLng(d.latitude, d.longitude),
                  name: allDevicesData.find((device: DeviceData) => device.dev_id === d.dev_id)?.dev_name || 'Unknown',
                  devId: d.dev_id,
                  dateTime: DateFormatWithSlash(new Date(d.tx_time)),
                  adc: d.src_02 ? calculateVoltage(d.src_02) : '',
                  machineType: machineType.find((m) => m.value == d.src_01)? machineType.find((m) => m.value == d.src_01)?.label : " 不明 ",
                  machineNum: d.src_05,
                  measuredVal: d.src_03,
                  unit: machineType.find((m) => m.value == d.src_01)? machineType.find((m) => m.value == d.src_01)?.unit : "",
                  err: errmsg.find((e) => e.value == d.src_04) ? errmsg.find((e) => e.value == d.src_04)?.label :"不明",
                  visible: true,
                  color: colorToUse,
                  initialColor: initialDevIdToColorMap[devId]
              }))

              if (validPinsForDevice.length > 0) {
                allPins = [...allPins, ...validPinsForDevice];
                dataAvailability[devId] = true;
              } else {
                dataAvailability[devId] = false;
              }

            } catch (error) {
                console.error(`Error fetching data for device ${devId}:`, error);
            }
        }
        setDeviceDataAvailability(dataAvailability);
        return allPins;
    }

    const handleSearchClick = async () => {
        if (startDate > endDate) {
            return alert('終了日は開始日より後にしてください。');
        }
        setIsLoading(true);
        const formatStartDate = startDate.toISOString();
        const formatEndDate = endDate.toISOString();
        polylines.forEach(polyline => polyline.remove());
        setPolylines([]);
        const newPins = await fetchDataForTimeRange(formatStartDate, formatEndDate, selectedDevIds);
        const usage: UsageRecord = {
            use_date: new Date().toISOString(),
            client_id: cIDSess != null ? parseInt(cIDSess) : 0,
            u_id: uId ? JSON.parse(uId).u_id : 0,
            event_type: 2,
            tile_count: 0
        };
        await addUsageRecord(usage);

        const updatedPins = newPins.map((pin) => {
          const colorToUse = colorFor(pin.devId);
          return { ...pin, color: colorToUse, initialColor: initialDevIdToColorMap[pin.devId] };
        });

        setPins(updatedPins);
        setIsDropdownOpen(false);
    };

    //----------------reset setting-------------------
    const reset = () => {
        fetchData();
        setStartDate(FormatStartDate(currentDate));
        setEndDate(FormatEndDate(currentDate));
        setIsDropdownOpen(false);
    };


    return (
        <div className="App">
            <Navbar collapseOnSelect expand="lg" className="navbar">
                <Navbar.Brand href="/eltres/tracking">ElTRES</Navbar.Brand>
                <Navbar.Toggle aria-controls="responsive-navbar-nav" />
                <Navbar.Offcanvas placement="end" style={{ width: "200px" }}>
                    <Offcanvas.Header closeButton>
                        <Offcanvas.Title>
                            ElTRES
                        </Offcanvas.Title>
                    </Offcanvas.Header>
                    <Offcanvas.Body>
                        <Nav className="justify-content-end flex-grow-1 pe-3 nav-tab">
                            <Nav.Link
                              as={NavLink}
                              to="/eltres/tracking"
                              state={{ selected: selectedDevIds, colorMap: currentColorMap }}
                            >
                              現在地
                            </Nav.Link>
                            <Nav.Link href="/eltres/movingHistory" style={{ color: '#bbbcbd' }}>移動履歴</Nav.Link>
                            {showAdminLinks && (
                                <>
                                    <Nav.Link href="/eltres/clients" hidden={isClientAdmin}>取引先設定</Nav.Link>
                                    <Nav.Link href="/eltres/users">ユーザ設定</Nav.Link>
                                    <Nav.Link href="/eltres/devices">端末設定</Nav.Link>
                                    <Nav.Link href="/eltres/payloads" hidden={isClientAdmin}>ペイロード設定</Nav.Link>
                                    <Nav.Link href="/eltres/usageAmount" hidden={isClientAdmin}>使用量</Nav.Link>
                                </>
                            )}
                            <Nav.Link href="/eltres/download">ダウンロード</Nav.Link>
                            <hr />
                            <Nav.Link href="/eltres/changePWD">パスワード変更</Nav.Link>
                            <Nav.Link onClick={() => uId ? Logout(uId) : moveTo('/eltres/login')}>ログアウト</Nav.Link>
                        </Nav>
                    </Offcanvas.Body>
                </Navbar.Offcanvas>
            </Navbar>

            <div className="map-container" ref={mapRef} style={{ position: 'relative', marginTop: '0px', width: '100%', height: '100vh' }}></div>

            <div className="pin-list" style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.5 : 1 }}>
                <button className="btn btn-viewAll" onClick={handleViewAllClick}>全面見る</button>
                <div className="custom-dropdown">
                    <button className="btn btn-dropdown" onClick={() => setIsDropdownOpen(!isDropdownOpen)} aria-controls="custom-dropdown-content" aria-expanded={isDropdownOpen}>デバイス</button>
                    <Collapse in={isDropdownOpen}>
                        <div className="custom-dropdown-content" id="custom-dropdown-content">
                            <button className="btn btn-search" onClick={() => handleSearchClick()}>検索</button>
                            <button className="btn btn-reset" onClick={reset}>リセット</button>
                            <LocalizationProvider dateAdapter={AdapterDateFns}>
                                <DateTimePicker
                                    label="Start"
                                    defaultValue={startDate}
                                    value={startDate}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setStartDate(newValue);
                                        }
                                    }}
                                    slotProps={{
                                        actionBar: {
                                            actions: ['cancel', 'accept'],
                                        },
                                    }}
                                    maxDate={endDate}
                                    format="yyyy/MM/dd HH:mm"
                                    closeOnSelect={false}
                                />
                                <DateTimePicker
                                    label="End"
                                    value={endDate}
                                    defaultValue={endDate}
                                    onChange={(newValue) => {
                                        if (newValue) {
                                            setEndDate(newValue);
                                        }
                                    }}
                                    slotProps={{
                                        actionBar: {
                                            actions: ['cancel', 'accept'],
                                        },
                                    }}
                                    minDate={startDate}
                                    maxDate={new Date()}
                                    format="yyyy/MM/dd HH:mm"
                                    closeOnSelect={false}
                                />
                            </LocalizationProvider>
                            <p></p>
                            <div className="device-list">
                                {allDevicesData.length > 0 ? (allDevicesData.map((device) => (
                                    <div key={device.dev_id} className="device-list-item">
                                        <input
                                            type="checkbox"
                                            id={`checkbox-${device.dev_id}`}
                                            className="checkbox-item"
                                            checked={selectedDevIds.includes(device.dev_id)}
                                            onChange={() => handleCheckboxChange(device.dev_id)}
                                            />
                                        {device.dev_name}
                                        {!deviceDataAvailability[device.dev_id] && <span className="device-x">✕</span>}
                                    </div >
                                ))) : (
                                    <div style={{ color: 'red' }}>ユーザーに紐づけてる装置の情報がありません。</div>
                                )}
                            </div>
                        </div>
                    </Collapse>
                </div>
            </div>
            {isLoading && (
              <div className="loading-overlay" aria-busy="true" aria-live="polite">
                <div className="spinner" />
                <div className="loading-text">読み込み中...</div>
              </div>
            )}
        </div>
    );
}

export default MovingHistory;